import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { buildBreadcrumbJsonLd } from "@/lib/breadcrumbSchema";

const SITE_URL = "https://coloradowebimpressions.com";
const LAST_UPDATED = "April 26, 2026";

export const Route = createFileRoute("/editorial-policy")({
  head: () => ({
    meta: [
        { name: "description", content: "Our editorial standards, sourcing rules, fact-checking process, and corrections policy for content published on Colorado Web Impressions." },
      { title: "Editorial Policy & Corrections | CWI" },
      {
        name: "description",
        content:
          "How we research, fact-check, update, and correct every article on the Learn (Blog). Owner-written, in-house, no AI-spun content.",
      },
      {
        property: "og:title",
        content: "Editorial Policy — Fact-Checking & Corrections",
      },
      {
        property: "og:description",
        content:
          "How Colorado Web Impressions researches, reviews, updates, and corrects every Learn (Blog) article.",
      },
      { property: "og:url", content: `${SITE_URL}/editorial-policy` },
      { property: "og:type", content: "article" },
      { name: "robots", content: "index, follow" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Editorial Policy — Fact-Checking & Corrections" },
      { name: "twitter:description", content: "How we research, fact-check, update, and correct every article on the Learn (Blog). Owner-written, in-house, no AI-spun content." },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/editorial-policy` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Editorial Policy",
          url: `${SITE_URL}/editorial-policy`,
          description:
            "Editorial standards, fact-checking process, correction policy, and update cadence for the Colorado Web Impressions Learn (Blog).",
          publisher: { "@id": `${SITE_URL}/#organization` },
          mainContentOfPage: {
            "@type": "WebPageElement",
            cssSelector: "article",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildBreadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Editorial Policy" },
          ]),
        ),
      },
    ],
  }),
  component: EditorialPolicyPage,
});

function EditorialPolicyPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Editorial Policy & Fact-Checking Standards"
        title="Editorial policy, fact-checking, and corrections"
        description={`Last updated: ${LAST_UPDATED}. Every article on the Learn (Blog) is owner-written, fact-checked, and dated. Here's exactly how we research, review, update, and correct what we publish.`}
      >
        <div className="flex justify-center">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Editorial Policy" }]} />
        </div>
      </PageHero>

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <article className="prose-article">
            <h2>Who writes the articles</h2>
            <p>
              Every article on the{" "}
              <Link to="/learn" className="font-bold text-brand-gold-deep underline">
                Learn (Blog)
              </Link>{" "}
              is written by Chris Heidlebaugh, owner of Colorado Web Impressions, with 25+
              years of hands-on experience building, ranking, and converting websites for
              Colorado small businesses. We do not outsource writing, we do not use
              AI-generated drafts as published content, and we do not publish guest posts or
              sponsored articles.
            </p>

            <h2>How we research and fact-check</h2>
            <ol>
              <li>
                <strong>Primary sources first.</strong> When we cite a Google policy,
                algorithm behavior, or platform rule, we link to or reference Google&rsquo;s
                own documentation, official help center articles, or public statements from
                named Search/Maps team members.
              </li>
              <li>
                <strong>Real client data.</strong> Pricing ranges, ranking timelines,
                conversion benchmarks, and review-volume numbers come from work we have
                actually shipped for Colorado clients — not from secondhand industry
                surveys.
              </li>
              <li>
                <strong>Owner review.</strong> Chris personally reviews every article before
                publication. Every claim about &ldquo;what works&rdquo; has been tested on a
                real Colorado business, not lifted from a SaaS company&rsquo;s blog.
              </li>
              <li>
                <strong>No affiliate or pay-to-play recommendations.</strong> When we name a
                tool, vendor, or platform, we do not earn a commission. If that ever
                changes, we will disclose it inline.
              </li>
            </ol>

            <h2>Update cadence</h2>
            <p>
              Local search, Google Business Profile, and AI search change quickly. Our
              update process:
            </p>
            <ul>
              <li>
                <strong>Quarterly review.</strong> Every Learn (Blog) article is re-read at
                least once per quarter and updated if anything material has changed.
              </li>
              <li>
                <strong>Event-driven updates.</strong> When Google ships a confirmed core
                update, a Maps policy change, or a meaningful AI search shift, affected
                articles are updated within 30 days.
              </li>
              <li>
                <strong>Visible dates.</strong> Every article shows its{" "}
                <em>Published</em> date and, when it differs, its <em>Updated</em> date, in
                proper <code>&lt;time&gt;</code> tags. The same dates are emitted in the
                Article schema for AI tools and search engines.
              </li>
            </ul>

            <h2>Correction policy</h2>
            <p>
              We get things wrong sometimes. When we do, we fix them in public, not
              quietly:
            </p>
            <ul>
              <li>
                <strong>Material errors</strong> (a wrong number, a wrong rule, a wrong
                quote, an out-of-date policy claim) get corrected in the article body and
                the <em>Updated</em> date is bumped.
              </li>
              <li>
                <strong>Significant corrections</strong> are noted with a brief
                &ldquo;Correction:&rdquo; line at the bottom of the article so readers can
                see what changed.
              </li>
              <li>
                <strong>Typos and small clarifications</strong> are fixed without a
                correction note, but the <em>Updated</em> date is still bumped.
              </li>
              <li>
                <strong>Removed content.</strong> We do not memory-hole articles. If a
                piece is no longer relevant, it is updated, redirected to a current article,
                or marked as archived — never silently deleted to break inbound links.
              </li>
            </ul>

            <h2>How to report an error or request a correction</h2>
            <p>
              If you find a mistake, an outdated claim, or a broken link in any Learn
              (Blog) article, please tell us. We read every message and respond personally.
            </p>
            <ul>
              <li>
                Email:{" "}
                <a
                  href="mailto:info@coloradowebimpressions.com?subject=Editorial%20correction"
                  className="font-bold text-brand-gold-deep underline"
                 aria-label="Email Colorado Web Impressions">
                  info@coloradowebimpressions.com
                </a>{" "}
                with the subject line &ldquo;Editorial correction.&rdquo;
              </li>
              <li>
                Phone:{" "}
                <a href="tel:+17194193935" className="font-bold text-brand-gold-deep underline" aria-label="Call (719) 419-3935">
                  (719) 419-3935
                </a>
              </li>
              <li>
                Or use our{" "}
                <Link to="/contact" className="font-bold text-brand-gold-deep underline">
                  contact form
                </Link>
                .
              </li>
            </ul>
            <p>
              Please include the article URL, the specific claim or sentence in question,
              and a source if you have one. We aim to acknowledge corrections within 2
              business days.
            </p>

            <h2>AI usage disclosure</h2>
            <p>
              We use AI tools internally for research, outline scaffolding, grammar
              checks, and image generation for diagrams and OG images. We do{" "}
              <strong>not</strong> publish AI-generated article bodies. Every paragraph on
              the Learn (Blog) was written or rewritten by Chris. If we ever change that
              policy, this page will be updated first.
            </p>

            <h2>Our standards in one sentence</h2>
            <p>
              <strong>
                If we wouldn&rsquo;t tell a paying client to do it, we won&rsquo;t publish
                it.
              </strong>
            </p>
          </article>
        </div>
      </section>
    </SiteLayout>
  );
}
